import { PropsWithChildren, Fragment, useEffect, useState } from 'react';

import { Navigate, useParams } from 'react-router-dom';
import { Params } from 'react-router';
import { NavigateProps } from 'react-router/dist/lib/components';
import { ApolloClient, gql, useApolloClient } from '@apollo/client';
import { LRUCache, PersistentCacheProvider } from '@devxdiscover/devhub-common';

const componentLookupCache = new LRUCache<string, string>({
  ttl: { hours: 2 },
  maxSize: 100,
  backingCache: new PersistentCacheProvider('componentLookup', 'local', false),
});
const templateLookupCache = new LRUCache<string, string>({
  ttl: { hours: 2 },
  maxSize: 100,
  backingCache: new PersistentCacheProvider('templateLookup', 'local', false),
});

type Interceptor = (
  params: Readonly<Params>,
  apolloClient: ApolloClient<object>,
) => Promise<NavigateProps | undefined>;

type InterceptProps = {
  canIntercept: (params: Readonly<Params>) => boolean;
  intercept: (
    params: Readonly<Params>,
    apolloClient: ApolloClient<object>,
  ) => Promise<NavigateProps | undefined>;
};

/**
 * A route that can intercept and redirect to a different route.
 * @param props - The properties of the component.
 * @constructor
 */
export const InterceptRoute = (props: PropsWithChildren<InterceptProps>) => {
  const params = useParams();
  const apolloClient = useApolloClient();
  const [redirect, setRedirect] = useState<NavigateProps | null>(null);

  useEffect(() => {
    const interceptRoute = async () => {
      if (props.canIntercept(params)) {
        const redirectProps = await props.intercept(params, apolloClient);
        if (redirectProps) {
          setRedirect(redirectProps);
        }
      }
    };

    interceptRoute();
  }, [params, apolloClient, props]);

  if (props.canIntercept(params) && redirect) {
    return <Navigate {...redirect} />;
  } else if (props.canIntercept(params) && !redirect) {
    return <Fragment />;
  }
  // Render the child component if we don't intercept
  return <Fragment>{props.children}</Fragment>;
};

/**
 * An interceptor that redirects to a page with a SHA instead of a name.
 * @param baseRoutePath - The base route path
 */
export const routeInterceptor: (baseRoutePath: string) => Interceptor = (
  baseRoutePath: string,
) => {
  return (async (params, client) => {
    if (!params.name) {
      return undefined;
    }
    if (componentLookupCache.has(params.name)) {
      return {
        to: `/${baseRoutePath}/${params.namespace}/${
          params.kind
        }/${componentLookupCache.get(params.name)}${
          params['*'] ? `/${params['*']}` : ''
        }`,
      };
    }
    const result = await client.query<{
      resources: { componentsPaged: { components: { id: string }[] } };
    }>({
      query: gql`
        query GetIdForName($filter: ResourceCatalogComponentFilter) {
          resources {
            componentsPaged(filter: $filter) {
              components {
                id
              }
            }
          }
        }
      `,
      variables: { filter: { name: params.name } },
    });
    if (
      (result.data?.resources?.componentsPaged?.components ?? []).length > 0
    ) {
      componentLookupCache.set(
        params.name,
        result.data.resources.componentsPaged.components[0].id,
      );
      return {
        to: `/${baseRoutePath}/${params.namespace}/${params.kind}/${
          result.data.resources.componentsPaged.components[0].id
        }${params['*'] ? `/${params['*']}` : ''}`,
      };
    }
    return undefined;
  }) as Interceptor;
};

/**
 * An interceptor that redirects to a page with a SHA instead of a name.
 * @param baseRoutePath - The base route path
 */
export const templateInterceptor: (baseRoutePath: string) => Interceptor = (
  baseRoutePath: string,
) => {
  return (async (params, client) => {
    if (!params.name) {
      return undefined;
    }

    // If this is a SHA, redirect to the default template page - no lookup needed
    if (/^[a-f0-9]{40}$/.test(params.name ?? '')) {
      return {
        to: `/${baseRoutePath}/templates/v2/default/${params.name}`,
      };
    }

    // Attempt to lookup the template ID by name
    if (templateLookupCache.has(params.name)) {
      return {
        to: `/${baseRoutePath}/templates/v2/default/${templateLookupCache.get(
          params.name,
        )}`,
      };
    }
    const result = await client.query<{
      resources: { devHubTemplatesPaged: { templates: { id: string }[] } };
    }>({
      query: gql`
        query GetIdForName($filter: ResourceCatalogDevHubTemplateFilter) {
          resources {
            devHubTemplatesPaged(filter: $filter) {
              templates {
                id
              }
            }
          }
        }
      `,
      variables: { filter: { name: params.name } },
    });
    if (
      (result.data?.resources?.devHubTemplatesPaged?.templates ?? []).length > 0
    ) {
      templateLookupCache.set(
        params.name,
        result.data.resources.devHubTemplatesPaged.templates[0].id,
      );
      return {
        to: `/${baseRoutePath}/templates/v2/default/${result.data.resources.devHubTemplatesPaged.templates[0].id}`,
      };
    }
    return undefined;
  }) as Interceptor;
};
